import { useEffect, useMemo, useState } from 'react'
import i18n from '@/i18n'
import { supabase } from '@/lib/supabase'
import type { AudioClip, Message, Ticket } from '@/lib/types'
import {
  X,
  AudioLines,
  Loader2,
  Captions,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Scissors,
} from 'lucide-react'
import { ClippedAudio } from './ClippedAudio'
import { useNotify } from './ConfirmDialog'

interface Props {
  open: boolean
  /** Audio media URLs of the ticket (already extracted with groupMedia). */
  urls: string[]
  /** Ticket the audios belong to — used to find the matching source messages. */
  ticket: Ticket | null
  /** Per-user pref: fire transcription automatically when opening the panel. */
  autoTranscribe: boolean
  onClose: () => void
}

/**
 * Compact panel that opens when the audio chip on a card is tapped. Lists the
 * card's audio messages, lets the user play each one, see its résumé /
 * transcription if already done, or trigger transcription on the fly.
 *
 * If the parent ticket carries `audio_clips` (= it was born from a split),
 * each audio is played only over its assigned `[start_sec, end_sec]` window
 * via the ClippedAudio component.
 */
export function AudioPanel({
  open,
  urls,
  ticket,
  autoTranscribe,
  onClose,
}: Props) {
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(false)
  const [transcribing, setTranscribing] = useState<string | null>(null)
  const [showVerbatim, setShowVerbatim] = useState<Record<string, boolean>>({})
  const notify = useNotify()

  useEffect(() => {
    if (!open || !ticket) return
    let cancelled = false
    setMessages([])
    setShowVerbatim({})
    if (ticket.source_message_ids.length === 0) {
      setLoading(false)
      return
    }
    setLoading(true)
    supabase
      .from('messages')
      .select('*')
      .in('id', ticket.source_message_ids)
      .order('received_at', { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) console.error('audio panel load', error)
        setMessages(data ?? [])
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, ticket])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  /** Pair each requested audio URL with its source message (if any). */
  const audioItems = useMemo(
    () =>
      urls.map((url) => ({
        url,
        message: messages.find((m) => m.media_urls.includes(url)),
      })),
    [urls, messages],
  )

  /** Index `ticket.audio_clips` by message_id so we can quickly look up the
   *  clip range when rendering each audio. */
  const clipByMessageId = useMemo<Record<string, AudioClip>>(() => {
    const m: Record<string, AudioClip> = {}
    for (const c of ticket?.audio_clips ?? []) m[c.message_id] = c
    return m
  }, [ticket?.audio_clips])

  // Auto-transcription: pick the next audio source message without a summary.
  // Re-fires after each one completes because messages updates.
  useEffect(() => {
    if (!open || !autoTranscribe || loading || transcribing) return
    const next = messages.find(
      (m) =>
        !m.summary &&
        m.media_urls.some((u) => urls.includes(u)),
    )
    if (next) transcribeMessage(next.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoTranscribe, loading, transcribing, messages, urls])

  async function transcribeMessage(messageId: string) {
    setTranscribing(messageId)
    try {
      const { data, error } = await supabase.functions.invoke('transcribe-audio', {
        body: { message_id: messageId, locale: i18n.language },
      })
      if (error) {
        let detail = error.message
        try {
          const ctx = (error as { context?: Response }).context
          const body =
            ctx && typeof ctx.json === 'function' ? await ctx.json() : null
          if (body?.error) detail = body.error
        } catch {
          /* keep generic */
        }
        throw new Error(detail)
      }
      const result = data as {
        transcription?: string
        summary?: string
        error?: string
      }
      if (result.error) throw new Error(result.error)
      setMessages((arr) =>
        arr.map((x) =>
          x.id === messageId
            ? {
                ...x,
                transcription: result.transcription ?? '',
                summary: result.summary || null,
              }
            : x,
        ),
      )
    } catch (e) {
      console.error('transcription failed', e)
      notify({
        title: 'Échec de la transcription',
        message: e instanceof Error ? e.message : String(e),
        tone: 'error',
      })
    } finally {
      setTranscribing(null)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-[var(--color-border-soft)] px-5 py-3.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]">
              <AudioLines className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h3 className="text-[14px] font-semibold text-[var(--color-text-strong)]">
                Audios ({urls.length})
              </h3>
              {ticket?.title && (
                <div className="truncate text-[11px] text-[var(--color-text-dim)]">
                  {ticket.title}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-1.5 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto p-4">
          {loading && messages.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-8 text-[12px] text-[var(--color-text-dim)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Chargement…
            </div>
          ) : (
            audioItems.map((item, idx) => {
              const m = item.message
              const date = m?.received_at
                ? new Date(m.received_at).toLocaleString('fr-FR', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })
                : null
              const verbatimOpen = showVerbatim[item.url] === true
              const isTranscribing = m ? transcribing === m.id : false
              const canTranscribe = !!m && !m.summary
              const clip = m ? clipByMessageId[m.id] : undefined

              return (
                <div
                  key={item.url}
                  className="rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface-2)] p-3"
                >
                  <div className="mb-2 flex items-center gap-2 text-[11px] text-[var(--color-text-dim)]">
                    <AudioLines className="h-3.5 w-3.5" />
                    <span className="font-medium text-[var(--color-text)]">
                      Message vocal{urls.length > 1 ? ` #${idx + 1}` : ''}
                    </span>
                    {date && <span>· {date}</span>}
                    {clip && (
                      <span
                        className="inline-flex items-center gap-1 rounded-md bg-[var(--color-progress-bg)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--color-link)]"
                        title={`Lecture bornée entre ${clip.start_sec.toFixed(1)}s et ${clip.end_sec.toFixed(1)}s`}
                      >
                        <Scissors className="h-2.5 w-2.5" />
                        {clip.start_sec.toFixed(1)}–{clip.end_sec.toFixed(1)}s
                      </span>
                    )}
                  </div>

                  <ClippedAudio
                    src={item.url}
                    startSec={clip?.start_sec}
                    endSec={clip?.end_sec}
                  />

                  {m?.summary && (
                    <div className="mt-2.5 rounded-xl bg-[var(--color-accent-bg)] px-3 py-2">
                      <div className="mb-0.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-accent-text)]">
                        <Sparkles className="h-3 w-3" />
                        Résumé
                      </div>
                      <div className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-[var(--color-text-strong)]">
                        {m.summary}
                      </div>
                    </div>
                  )}

                  {m?.transcription && (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() =>
                          setShowVerbatim((s) => ({
                            ...s,
                            [item.url]: !verbatimOpen,
                          }))
                        }
                        className="inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)] transition-colors hover:text-[var(--color-text)]"
                      >
                        {verbatimOpen ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                        <Captions className="h-3 w-3" />
                        Transcription complète
                      </button>
                      {verbatimOpen && (
                        <div className="mt-1 whitespace-pre-wrap rounded-xl bg-[var(--color-surface)] px-3 py-2 text-[12.5px] leading-relaxed text-[var(--color-text)]">
                          {m.transcription}
                        </div>
                      )}
                    </div>
                  )}

                  {canTranscribe && (
                    <button
                      type="button"
                      onClick={() => transcribeMessage(m.id)}
                      disabled={isTranscribing}
                      className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[11.5px] font-medium text-[var(--color-text)] transition-all hover:border-[var(--color-link)] disabled:opacity-60"
                    >
                      {isTranscribing ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Captions className="h-3.5 w-3.5" />
                      )}
                      Transcrire
                    </button>
                  )}

                  {!m && !loading && (
                    <div className="mt-2 text-[11px] italic text-[var(--color-text-dim)]">
                      Pièce jointe sans message source — la transcription n'est
                      pas possible.
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
